import React, { useCallback, useMemo } from "react";
import { MilestoneTasks } from "../../../../../../../../../api/homeSiteMilestones/types";
import { CombinedMilestoneData, CombinedMilestoneTask } from "../types";
import { useCreateMilestoneTask } from "../../../../../../../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";

// ============================================================================
// TYPES
// ============================================================================
type TaskPayload = Partial<MilestoneTasks["data"][number]>;
type MilestoneState = { [key: string]: boolean };

// ============================================================================
// HOOK INTERFACE
// ============================================================================
interface UseMilestoneActionsProps {
     combinedMilestoneData: CombinedMilestoneData;
     getTasksForMilestone: (milestoneName: string) => CombinedMilestoneTask[];
     findAutomatedTasks: (tasks: CombinedMilestoneTask[]) => CombinedMilestoneTask[];
     selectedMilestones: MilestoneState;
     setSelectedMilestones: React.Dispatch<React.SetStateAction<MilestoneState>>;
     taskStates: MilestoneState;
     setTaskStates: React.Dispatch<React.SetStateAction<MilestoneState>>;
     updateMilestoneTasks: (tasks: TaskPayload[]) => void;
     userId: string;
     homeSiteId: string;
     saleDate: string | null | undefined;
     closeOfEscrowActual: string | null | undefined;
     closeOfEscrowEstimated: string | null | undefined;
     selectedMilestoneId: string | null;
     handleMenuClose: () => void;
     divisionCode: string;
}

export const useMilestoneActions = ({
     combinedMilestoneData,
     getTasksForMilestone,
     findAutomatedTasks,
     selectedMilestones,
     setSelectedMilestones,
     taskStates,
     setTaskStates,
     updateMilestoneTasks,
     userId,
     homeSiteId,
     saleDate,
     closeOfEscrowActual,
     closeOfEscrowEstimated,
     selectedMilestoneId,
     handleMenuClose,
     divisionCode
}: UseMilestoneActionsProps) => {
     // ============================================================================
     // MEMOIZED DATA STRUCTURES
     // ============================================================================

     // Memoize milestone lookup for better performance
     const milestoneMap = useMemo(() => {
          const map = new Map<string, typeof combinedMilestoneData[0]>();
          combinedMilestoneData?.forEach(milestone => {
               const milestoneId = milestone.milestoneName;
               map.set(milestoneId, milestone);
          });
          return map;
     }, [combinedMilestoneData]);

     // Initialize the create milestone task mutation hook
     const createMilestoneTaskMutation = useCreateMilestoneTask(divisionCode);

     // ============================================================================
     // UTILITY FUNCTIONS
     // ============================================================================

     // Memoize task processing functions for better performance
     const processTasksForMilestone = useCallback((milestoneTasks: CombinedMilestoneTask[]) => {
          const automatedTasks = findAutomatedTasks(milestoneTasks);
          const automatedTaskNames = new Set(automatedTasks.map(task => task.divisionTemplate.taskName));

          return milestoneTasks.filter(task =>
               !automatedTaskNames.has(task.divisionTemplate.taskName)
          );
     }, [findAutomatedTasks]);

     // Helper function to filter enabled tasks
     const filterEnabledTasks = useCallback((tasks: CombinedMilestoneTask[]) => {
          return tasks.filter(task => task.divisionTemplate?.status !== false && !task.divisionTemplate.automated);
     }, []);

     // Helper function to separate tasks for creation vs update
     const separateTasksForOperation = useCallback((
          tasks: CombinedMilestoneTask[],
          needsUpdate: (task: CombinedMilestoneTask) => boolean
     ) => {
          const tasksToCreate = tasks.filter(task =>
               needsUpdate(task) && !task.milestoneTaskData?.id
          );
          const tasksToUpdate = tasks.filter(task =>
               needsUpdate(task) && task.milestoneTaskData?.id
          );
          return { tasksToCreate, tasksToUpdate };
     }, []);

     // ============================================================================
     // PAYLOAD CREATION FUNCTIONS
     // ============================================================================

     // Memoize payload creation to avoid repeated object creation
     const createTaskPayload = useCallback((
          task: CombinedMilestoneTask,
          status: boolean | null,
          completionDate: Date | null = null
     ): TaskPayload => ({
          id: task.milestoneTaskData?.id || '',
          homeSiteId,
          divisionCode: task.divisionTemplate.divisionCode,
          mileStoneName: task.divisionTemplate.mileStoneName,
          updatedBy: userId,
          status,
          completionDate,
          saleDate,
          closeOfEscrowActual,
          closeOfEscrowEstimated
     }), [userId, homeSiteId, saleDate, closeOfEscrowActual, closeOfEscrowEstimated]);

     // Helper function to create new task payload
     const createNewTaskPayload = useCallback((task: CombinedMilestoneTask) => ({
          divisionCode: task.divisionTemplate.divisionCode,
          taskName: task.divisionTemplate.taskName,
          homeSiteId: homeSiteId,
          mileStoneName: task.divisionTemplate.mileStoneName,
          conciergeEmail: '',
          saleDate: saleDate,
          closeOfEscrowActual: closeOfEscrowActual,
          closeOfEscrowEstimated: closeOfEscrowEstimated,
          createdBy: userId,
          updatedBy: userId,
          taskCode: task.divisionTemplate.taskCode,
     }), [homeSiteId, userId, saleDate, closeOfEscrowActual, closeOfEscrowEstimated]);

     // ============================================================================
     // STATE UPDATE FUNCTIONS
     // ============================================================================

     // Memoize task state update function to reduce re-renders
     const updateTaskStates = useCallback((taskUpdates: MilestoneState) => {
          setTaskStates(prev => ({ ...prev, ...taskUpdates }));
     }, [setTaskStates]);

     // ============================================================================
     // TASK OPERATION FUNCTIONS
     // ============================================================================

     // Helper function to process task creation
     const processTaskCreation = useCallback((tasksToCreate: CombinedMilestoneTask[]) => {
          tasksToCreate.forEach(task => {
               const newTask = createNewTaskPayload(task);
               const taskWithStatus = {
                    ...newTask,
                    status: true,
                    completionDate: new Date(),
               };

               createMilestoneTaskMutation.mutate(taskWithStatus);
          });
     }, [createNewTaskPayload, createMilestoneTaskMutation]);

     // Helper function to process task updates
     const processTaskUpdates = useCallback((
          tasksToUpdate: CombinedMilestoneTask[],
          newStatus: boolean | null,
          completionDate: Date | null
     ) => {
          const payload = tasksToUpdate.map(task =>
               createTaskPayload(task, newStatus, completionDate)
          );

          if (payload.length > 0) {
               updateMilestoneTasks(payload);
          }
     }, [createTaskPayload, updateMilestoneTasks]);

     // ============================================================================
     // EVENT HANDLERS
     // ============================================================================

     const handleCheckboxChange = useCallback((id: string) => {
          const newSelectedState = !selectedMilestones[id];
          setSelectedMilestones(prev => ({ ...prev, [id]: newSelectedState }));

          const milestoneToUpdate = milestoneMap.get(id);
          if (!milestoneToUpdate) return;

          const milestoneTasks = getTasksForMilestone(milestoneToUpdate.milestoneName);
          if (!milestoneTasks.length) return;

          const allTasks = milestoneTasks;
          const filteredTasks = processTasksForMilestone(milestoneTasks);

          const tasksToProcess = filterEnabledTasks(allTasks);

          const taskStateUpdates: MilestoneState = {};
          filteredTasks.forEach(task => {
               if (task.divisionTemplate?.status !== false) {
                    taskStateUpdates[task.divisionTemplate.taskName] = newSelectedState;
               }
          });

          updateTaskStates(taskStateUpdates);

          const needsUpdate = (task: CombinedMilestoneTask) => {
               const currentStatus = task.milestoneTaskData?.status;
               return newSelectedState ? currentStatus !== true : currentStatus === true;
          };

          const { tasksToCreate, tasksToUpdate } = separateTasksForOperation(tasksToProcess, needsUpdate);

          processTaskCreation(tasksToCreate);
          processTaskUpdates(tasksToUpdate, newSelectedState ? true : null, newSelectedState ? new Date() : null);
     }, [
          milestoneMap,
          getTasksForMilestone,
          processTasksForMilestone,
          selectedMilestones,
          setSelectedMilestones,
          updateTaskStates,
          filterEnabledTasks,
          separateTasksForOperation,
          processTaskCreation,
          processTaskUpdates
     ]);

     const handleTaskCheckboxChange = useCallback((task: CombinedMilestoneTask) => {
          const taskName = task.divisionTemplate.taskName;
          const currentState = taskStates[taskName];
          const newState = !currentState;

          setTaskStates(prev => ({
               ...prev,
               [taskName]: newState,
          }));

          const hasExistingRecord = task.milestoneTaskData?.id;

          if (hasExistingRecord) {
               const payload = createTaskPayload(
                    task,
                    newState ? true : null,
                    newState ? new Date() : null
               );

               updateMilestoneTasks([payload]);
          } else {
               const newTask = createNewTaskPayload(task);

               const taskWithStatus = {
                    ...newTask,
                    status: newState ? true : null,
                    completionDate: newState ? new Date() : null,
               };

               if (newState) {
                    taskWithStatus.completionDate = new Date();
                    taskWithStatus.status = true;
               } else {
                    taskWithStatus.completionDate = null;
                    taskWithStatus.status = null;
               }

               createMilestoneTaskMutation.mutate(taskWithStatus);
          }
     }, [taskStates, setTaskStates, createTaskPayload, updateMilestoneTasks, createNewTaskPayload, createMilestoneTaskMutation]);

     const handleNotApplicableMilestone = useCallback(() => {
          const milestoneToUpdate = milestoneMap.get(selectedMilestoneId || '');
          if (!milestoneToUpdate) return;

          const milestoneTasks = getTasksForMilestone(milestoneToUpdate.milestoneName);
          if (!milestoneTasks.length) return;

          const filteredTasks = processTasksForMilestone(milestoneTasks);

          // Filter out tasks with divisionStatus=false
          const tasksToProcess = filterEnabledTasks(filteredTasks);

          const isCurrentlyNotApplicable = tasksToProcess.every(
               task => task.milestoneTaskData?.status === false && task.milestoneTaskData?.completionDate !== null
          );

          if (isCurrentlyNotApplicable) {
               const tasksToUpdate = tasksToProcess.filter(task => task.milestoneTaskData?.id);
               processTaskUpdates(tasksToUpdate, null, null);

               const taskStateUpdates: MilestoneState = {};
               tasksToProcess.forEach(task => {
                    taskStateUpdates[task.divisionTemplate.taskName] = false;
               });
               updateTaskStates(taskStateUpdates);
          } else {
               const tasksToCreate = tasksToProcess.filter(task => !task.milestoneTaskData?.id);
               const tasksToUpdate = tasksToProcess.filter(task => task.milestoneTaskData?.id);

               tasksToCreate.forEach(task => {
                    const newTask = createNewTaskPayload(task);
                    const taskWithStatus = {
                         ...newTask,
                         status: false,
                         completionDate: new Date(),
                    };

                    createMilestoneTaskMutation.mutate(taskWithStatus);
               });

               if (tasksToUpdate.length > 0) {
                    processTaskUpdates(tasksToUpdate, false, new Date());
               }

               const taskStateUpdates: MilestoneState = {};
               tasksToProcess.forEach(task => {
                    taskStateUpdates[task.divisionTemplate.taskName] = true;
               });
               updateTaskStates(taskStateUpdates);
          }

          handleMenuClose();
     }, [
          milestoneMap,
          selectedMilestoneId,
          getTasksForMilestone,
          processTasksForMilestone,
          filterEnabledTasks,
          processTaskUpdates,
          createNewTaskPayload,
          createMilestoneTaskMutation,
          updateTaskStates,
          handleMenuClose
     ]);

     // ============================================================================
     // RETURN OBJECT
     // ============================================================================

     return {
          handleCheckboxChange,
          handleTaskCheckboxChange,
          handleNotApplicableMilestone,
     };
}; 